import React, { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Volume2, Mic, Speaker, AlertCircle } from "lucide-react";

interface AudioDevice {
  deviceId: string;
  label: string;
  kind: string;
}

export default function SettingsTab() {
  const [audioInputs, setAudioInputs] = useState<AudioDevice[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<AudioDevice[]>([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>("");
  const [selectedRingingDevice, setSelectedRingingDevice] = useState<string>("");
  const [selectedOutputDevice, setSelectedOutputDevice] = useState<string>("");
  const [permissionGranted, setPermissionGranted] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const requestPermissions = async () => {
    setLoading(true);
    setError("");

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      setPermissionGranted(true);
      await loadDevices();
    } catch (err) {
      console.error("Error requesting permissions:", err);
      setError("Microphone access denied. Please allow microphone access to configure audio settings.");
      setPermissionGranted(false);
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const inputs = devices
        .filter((device) => device.kind === "audioinput")
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          kind: device.kind,
        }));

      const outputs = devices
        .filter((device) => device.kind === "audiooutput")
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.slice(0, 8)}`,
          kind: device.kind,
        }));

      setAudioInputs(inputs);
      setAudioOutputs(outputs);

      // Set defaults if not already set
      if (inputs.length > 0 && !selectedMicrophone) {
        setSelectedMicrophone(inputs[0].deviceId);
      }
      if (outputs.length > 0 && !selectedRingingDevice) {
        setSelectedRingingDevice(outputs[0].deviceId);
      }
      if (outputs.length > 0 && !selectedOutputDevice) {
        setSelectedOutputDevice(outputs[0].deviceId);
      }

      const savedMic = localStorage.getItem("selectedMicrophone");
      const savedRinging = localStorage.getItem("selectedRingingDevice");
      const savedOutput = localStorage.getItem("selectedOutputDevice");

      if (savedMic && inputs.find((d) => d.deviceId === savedMic)) {
        setSelectedMicrophone(savedMic);
      }
      if (savedRinging && outputs.find((d) => d.deviceId === savedRinging)) {
        setSelectedRingingDevice(savedRinging);
      }
      if (savedOutput && outputs.find((d) => d.deviceId === savedOutput)) {
        setSelectedOutputDevice(savedOutput);
      }
    } catch (err) {
      console.error("Error loading devices:", err);
      setError("Failed to load audio devices.");
    }
  };

  const handleMicrophoneChange = (deviceId: string) => {
    setSelectedMicrophone(deviceId);
    localStorage.setItem("selectedMicrophone", deviceId);
    console.log("Selected microphone:", deviceId);
  };

  const handleRingingDeviceChange = (deviceId: string) => {
    setSelectedRingingDevice(deviceId);
    localStorage.setItem("selectedRingingDevice", deviceId);
    console.log("Selected ringing device:", deviceId);
  };

  const handleOutputDeviceChange = (deviceId: string) => {
    setSelectedOutputDevice(deviceId);
    localStorage.setItem("selectedOutputDevice", deviceId);
    console.log("Selected output device:", deviceId);
  };

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const hasLabels = devices.some((device) => device.label !== "");
      if (hasLabels) {
        setPermissionGranted(true);
        loadDevices();
      }
    });

    const handleDeviceChange = () => {
      if (permissionGranted) {
        loadDevices();
      }
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
    };
  }, [permissionGranted]);

  if (!permissionGranted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <h3 className="text-lg font-semibold dark:text-white">Microphone Access Required</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          To configure audio settings, we need access to your microphone and speakers.
        </p>
        <Button onClick={requestPermissions} disabled={loading}>
          {loading ? "Requesting..." : "Grant Permissions"}
        </Button>
        {error && (
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold dark:text-white">Audio Settings</h2>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2 dark:text-white">
          <Volume2 className="h-4 w-4" />
          Ringing Device
        </label>
        <Select value={selectedRingingDevice} onValueChange={handleRingingDeviceChange}>
          <SelectTrigger className="dark:text-white">
            <SelectValue placeholder="Select ringing device" />
          </SelectTrigger>
          <SelectContent>
            {audioOutputs.map((device) => (
              <SelectItem key={device.deviceId} value={device.deviceId}>
                {device.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2 dark:text-white">
          <Speaker className="h-4 w-4" />
          Output Device
        </label>
        <Select value={selectedOutputDevice} onValueChange={handleOutputDeviceChange}>
          <SelectTrigger className="dark:text-white">
            <SelectValue placeholder="Select output device" />
          </SelectTrigger>
          <SelectContent>
            {audioOutputs.map((device) => (
              <SelectItem key={device.deviceId} value={device.deviceId}>
                {device.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2 dark:text-white">
          <Mic className="h-4 w-4" />
          Microphone
        </label>
        <Select value={selectedMicrophone} onValueChange={handleMicrophoneChange}>
          <SelectTrigger className="dark:text-white">
            <SelectValue placeholder="Select microphone" />
          </SelectTrigger>
          <SelectContent>
            {audioInputs.map((device) => (
              <SelectItem key={device.deviceId} value={device.deviceId}>
                {device.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="pt-4 border-t border-border">
        <Button variant="outline" onClick={loadDevices} className="w-full dark:text-white">
          Refresh Devices
        </Button>
      </div>
    </div>
  );
}
